// M8-ASSESS: the screens' pure helpers — labels and tones for round and respondent states, the
// progress line, why a reminder is off, the proposed chart grouped by seat, the diff's counts,
// and the respondent page's answer edits. No React, no API: unit-tested in assess-model.test.ts.
import type { OperationOutput } from "@companyos/sdk";

export type RoundSummary = OperationOutput<"assessments.listRounds">["data"][number];
export type RoundDetail = OperationOutput<"assessments.getRound">;
export type Participant = RoundDetail["participants"][number];
export type ReportView = OperationOutput<"assessments.getReport">;
export type ReportBody = ReportView["body"];
export type Proposal = ReportView["proposals"][number];
export type DiffLine = OperationOutput<"assessments.previewApply">["lines"][number];
export type QuestionnaireView = OperationOutput<"respond.get">;
export type Answers = QuestionnaireView["answers"];
export type TaskAnswer = Answers["tasks"][number];
export type Step = QuestionnaireView["steps"][number];

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export const ROUND_STATE: Record<RoundSummary["state"], { label: string; tone: Tone }> = {
  draft: { label: "Draft", tone: "neutral" },
  open: { label: "Open", tone: "info" },
  closed: { label: "Closed", tone: "warning" },
  reported: { label: "Reported", tone: "success" },
};

export const PARTICIPANT_STATE: Record<Participant["state"], { label: string; tone: Tone }> = {
  invited: { label: "Pending", tone: "neutral" },
  opened: { label: "Opened", tone: "info" },
  in_progress: { label: "In progress", tone: "accent" },
  completed: { label: "Responded", tone: "success" },
  expired: { label: "Didn't answer", tone: "warning" },
};

export const QUESTIONNAIRE_LABEL: Record<string, string> = {
  full: "Full team X-Ray",
  delta: "Quick scan",
};

/** "4 of 6 responded" (or "No one asked yet"). */
export function progressLine(r: Pick<RoundSummary, "progress" | "respondents">): string {
  if (r.respondents === 0) return "No one asked yet";
  return `${r.progress.completed} of ${r.respondents} responded`;
}

/** The share answered (0..100) for the round's progress bar. */
export function progressPercent(r: Pick<RoundSummary, "progress" | "respondents">): number {
  return r.respondents === 0 ? 0 : Math.round((r.progress.completed / r.respondents) * 100);
}

/** Why the Remind button is off for a respondent (null: it is on). */
export function remindBlocked(
  round: Pick<RoundDetail, "state">,
  p: Pick<Participant, "state" | "reminder_count" | "can_remind">,
): string | null {
  if (p.can_remind) return null;
  if (round.state !== "open") return "The round is not open";
  if (p.state === "completed") return "Already responded";
  if (p.state === "expired") return "The link expired";
  if (p.reminder_count >= 2) return "Reminded twice already";
  return "Wait 20 h between reminders";
}

/** A seat in the proposed chart: a proposed seat (with what hangs on it) or an existing one. */
export type ProposalGroup = {
  key: string;
  title: string;
  /** The seat proposal itself (null for an existing seat). */
  seat: Proposal | null;
  /** Where it sits: "Reports to Recruiting lead", or the existing seat's title. */
  under: string | null;
  items: Proposal[];
};

/** Proposals grouped by the seat they land on: new seats first, then existing seats. */
export function groupProposals(proposals: readonly Proposal[]): ProposalGroup[] {
  const groups = new Map<string, ProposalGroup>();
  for (const p of proposals.filter((x) => x.kind === "seat"))
    groups.set(p.key, {
      key: p.key,
      title: String(p.payload.title ?? p.title),
      seat: p,
      under: p.seat_title ? `Reports to ${p.seat_title}` : "At the top of the chart",
      items: [],
    });
  for (const p of proposals.filter((x) => x.kind !== "seat")) {
    if (p.depends_on && groups.has(p.depends_on)) {
      groups.get(p.depends_on)?.items.push(p);
      continue;
    }
    const seatId = typeof p.payload.seat_id === "string" ? p.payload.seat_id : "none";
    const key = `existing:${seatId}`;
    const g = groups.get(key) ?? {
      key,
      title: p.seat_title ?? "Existing seat",
      seat: null,
      under: "Existing seat",
      items: [],
    };
    g.items.push(p);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => Number(!!b.seat) - Number(!!a.seat));
}

/** "3 to create · 1 reused · 2 skipped". */
export function diffSummary(c: { create: number; reuse: number; skip: number }): string {
  const parts = [
    c.create ? `${c.create} to create` : null,
    c.reuse ? `${c.reuse} reused` : null,
    c.skip ? `${c.skip} skipped` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Nothing to apply";
}

export const DIFF_KIND_LABEL: Record<DiffLine["kind"], string> = {
  role: "Job role",
  seat: "Seat",
  responsibility: "Responsibility",
  metric: "KPI",
  seat_metric: "KPI on seat",
};

export const HEALTH_TONE: Record<string, Tone> = {
  green: "success",
  yellow: "warning",
  red: "danger",
  grey: "neutral",
};
export const HEALTH_LABEL: Record<string, string> = {
  green: "Healthy",
  yellow: "Watch",
  red: "At risk",
  grey: "Didn't take part",
};
export const LOAD_LABEL: Record<string, string> = {
  overloaded: "Over capacity",
  underutilized: "Has room",
  balanced: "Balanced",
  unknown: "Unknown",
};
export const FIT_LABEL: Record<string, string> = {
  high: "Poor fit",
  moderate: "Mixed fit",
  low: "Good fit",
  none: "Good fit",
};
export const SEVERITY_TONE: Record<string, Tone> = {
  high: "danger",
  medium: "warning",
  low: "neutral",
};

/** An energy score (-100..100) in words. */
export function energyWord(score: number): string {
  if (score >= 30) return "Energised";
  if (score >= 0) return "Neutral";
  if (score >= -30) return "Draining";
  return "Very draining";
}

/** `1.18` → "118%". */
export function loadPercent(index: number | null): string {
  return index === null ? "—" : `${Math.round(index * 100)}%`;
}

export const hours = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${Math.round(n * 10) / 10} h`;

// Respondent page --------------------------------------------------------------------------

export const STEP_LABEL: Record<Step, string> = {
  role: "Your role",
  tasks: "Your tasks",
  ratings: "Each task",
  capacity: "Your week",
  management: "Your team",
  friction: "What slips",
};

export const blankTask = (): TaskAnswer => ({
  name: "",
  hours_per_week: null,
  energy: null,
  enjoy: null,
  skill: null,
  pillar: null,
  someone_else: false,
  why: null,
});

/** Replaces one task's fields. */
export function editTask(a: Answers, i: number, patch: Partial<TaskAnswer>): Answers {
  return { ...a, tasks: a.tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)) };
}

/** The tasks with a name (the ones the ratings step asks about). */
export const namedTasks = (a: Answers) =>
  a.tasks.map((t, i) => ({ t, i })).filter((x) => x.t.name.trim().length > 0);

/** Field errors of a submit, by the step they belong to. */
export function stepOfError(path: string): Step {
  if (path.startsWith("clarity")) return "role";
  if (path === "tasks") return "tasks";
  if (path.startsWith("tasks.")) return "ratings";
  if (path.startsWith("capacity")) return "capacity";
  if (path.startsWith("management")) return "management";
  return "friction";
}

/** Whether a step's own answers let the respondent move on (a light check; submit is the gate). */
export function stepReady(step: Step, a: Answers, full: boolean): boolean {
  switch (step) {
    case "role":
      return !!a.role_title?.trim() && Object.keys(a.clarity ?? {}).length === 4;
    case "tasks":
      return namedTasks(a).length > 0;
    case "ratings":
      return namedTasks(a).every(
        ({ t }) =>
          !!t.hours_per_week &&
          t.energy !== null &&
          !!t.enjoy &&
          !!t.skill &&
          !!t.pillar &&
          (!full || (t.why ?? "").trim().length >= 12),
      );
    case "capacity":
      return !!a.capacity_hours && !!a.load_band;
    case "management":
      return (
        !!a.management &&
        a.management.mgmt_share !== null &&
        !!a.management.team_load_read &&
        a.management.team_clarity_read !== null &&
        a.management.meeting_overhead_hours !== null
      );
    case "friction":
      return !!a.friction && Object.values(a.friction).some((v) => v.trim().length > 0);
  }
}
